import {
  PdfDocumentType,
  RenderingField,
  RenderingFieldGroup,
  RenderingSection,
  RenderingSubsection,
} from '@zinnia/form-engine-sdk';
import { DynamicPdfType } from '@breathelife/types';

import { pdfTypeLookup } from '../types';

// A node with no override inherits its parent's effective PDF types; one with an override
// must explicitly include the target. Because pruning only ever descends into kept nodes,
// this is equivalent to intersecting with the (already-verified) parent effective set.
//
// Each node is kept or dropped based solely on its own tag, never on what happens to its
// children: a subsection's own `text` (e.g. legal/consent copy) is independent of its field
// groups, so a subsection must not disappear just because all of its fields were narrowed
// away to a different PDF.
function keepsDocumentType(documentTypes: PdfDocumentType[] | undefined, target: PdfDocumentType): boolean {
  return !documentTypes?.length || documentTypes.includes(target);
}

function pruneFields(fields: RenderingField[], target: PdfDocumentType): RenderingField[] {
  return fields.filter((field) => keepsDocumentType(field.documentTypes, target));
}

function pruneFieldGroups(fieldGroups: RenderingFieldGroup[], target: PdfDocumentType): RenderingFieldGroup[] {
  return fieldGroups
    .filter((fieldGroup) => keepsDocumentType(fieldGroup.documentTypes, target))
    .map((fieldGroup) => ({ ...fieldGroup, fields: pruneFields(fieldGroup.fields, target) }));
}

function pruneSubsections(subsections: RenderingSubsection[], target: PdfDocumentType): RenderingSubsection[] {
  return subsections
    .filter((subsection) => keepsDocumentType(subsection.documentTypes, target))
    .map((subsection) => ({ ...subsection, fieldGroups: pruneFieldGroups(subsection.fieldGroups, target) }));
}

export function filterSectionsByPdfType(sections: RenderingSection[], pdfType: DynamicPdfType): RenderingSection[] {
  const target = pdfTypeLookup[pdfType];

  return sections
    .filter((section) => section.documentTypes.includes(target))
    .map((section) => ({ ...section, subsections: pruneSubsections(section.subsections, target) }));
}
